import React from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  ElementsConsumer,
  AddressElement,
} from '@stripe/react-stripe-js';
import Alert from 'react-s-alert';
import { themes } from 'styles/themes/wikifactoryNew';
import { staticURL } from 'core/url';

import {
  InputWrapper,
  WrappedInput,
  Label,
  FormWrapper,
  FormField,
  FormTitle,
  FormDivider,
} from 'libs/forms/styles';

// import ProximaNova from 'assets/fonts/proximanova-regular.woff2';
import { Center } from 'legacyComponents/styled/lib';
import { ProTip } from 'apps/components/shared';
// FIXME: To remove ThemeColors import
import ThemeColors from 'styles/themes/wikifactory/styles/colors';

import { Button, DisabledWrapper } from 'components';
import { brandIcons, CardLogo } from 'apps/payment/views/creditCard';

import styled from 'styled-components';

import { stripeStore } from 'apps/payment/stripe';
import { clientStore } from 'apps/client/app';
import { isFunction } from 'lodash';

// Docs - https://stripe.com/docs/stripe-js/reference#element-options
const style = {
  base: {
    color: '#000',
    fontSize: '15px',
    fontWeight: 400,

    '::placeholder': {
      color: ThemeColors.wfGrey400,
      fontWeight: 400,
      fontSize: '14px',
    },
  },
  invalid: {
    color: ThemeColors.red,
    borderColor: ThemeColors.red,
    iconColor: ThemeColors.destructive,
  },
};

// NOTE - Fonts won't load unless they come from a HTTPS source
export const fonts = [
  {
    family: 'ProximaNova',
    src: `local("ProximaNova"), local("Proxima Nova"), url("XXX") format("woff2")`,
  },
];

@withNamespaces()
@observer
export class StripeCardForm extends React.Component {
  constructor(props) {
    super(props);
    const { paymentMethodsManager } = props;
    paymentMethodsManager.ResetNewCard();
  }

  state = {
    hasSubmitted: false,
  };

  onNameChange = e => {
    const {
      paymentMethodsManager: { newCard },
    } = this.props;
    newCard.SetName(e.target.value);
  };

  submit = async onSubmitCallback => {
    const {
      paymentMethodsManager: { newCard },
      successAction,
    } = this.props;

    this.setState({
      hasSubmitted: true,
    });

    await newCard.Submit(this.props.stripe, this.props.elements);
    if (newCard.name && successAction) successAction();

    if (onSubmitCallback) {
      onSubmitCallback();
    }
  };

  render() {
    const {
      paymentMethodsManager,
      responsive,
      t,
      renderCardLogo = true,
      onCancel,
      saveProps = {
        size: 'small',
        text: t('Add'),
      },
      onSubmitCallback,
      footer,
      hideAddButton = false,
      handleChangeAddress,
    } = this.props;

    const { newCard } = paymentMethodsManager;
    const { hasSubmitted } = this.state;
    const loading = saveProps?.loading || newCard.submitting;

    return (
      <>
        <CardsWrapper>
          {renderCardLogo && (
            <Center>
              {Object.keys(brandIcons)
                .slice(0, -1)
                .map((brand, index) => (
                  <CardLogo
                    key={index}
                    src={staticURL(
                      `/assets/creditCards/mono/${brandIcons[brand]}`,
                    )}
                    mono
                  />
                ))}
            </Center>
          )}
        </CardsWrapper>
        {this.props.expandStripeTop &&
          this.props.expandStripeTop(this.submit, newCard.submitting)}
        <CardFormWrapper
          posting={newCard.submitting}
          disabled={loading}
          responsive={responsive}
        >
          <FormField>
            <CardLabel>{'Card Holder Name'}</CardLabel>
            <InputWrapper
              borderColor={
                hasSubmitted && !newCard.name
                  ? ThemeColors.red
                  : ThemeColors.wfGrey500
              }
              padding='7px 10px'
              responsive={responsive}
            >
              <WrappedInput
                disabled={loading}
                data-testid='paymentDetailPage-cardHolderName-input'
                onChange={this.onNameChange}
                value={newCard.name}
                placeholder={t('Exactly as it appears')}
                autoFocus
                borderColor={ThemeColors.wfGrey500}
              />
            </InputWrapper>
          </FormField>
          <FormField marginTop='0px' paddingTop='15px'>
            <CardLabel>{t('Card Number')}</CardLabel>
            <DisabledWrapper disabled={loading}>
              <CardNumberElement options={{ style, disabled: loading }} />
            </DisabledWrapper>
          </FormField>
          <CardDetails
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '1rem',
              marginTop: 15,
            }}
          >
            <FormWrapper noPadding responsive>
              <CardLabel>{t('Expiration Date')}</CardLabel>
              <DisabledWrapper disabled={loading}>
                <CardExpiryElement options={{ style, disabled: loading }} />
              </DisabledWrapper>
            </FormWrapper>
            <FormWrapper noPadding responsive>
              <CardLabel>{t('Card Security Code')}</CardLabel>
              <DisabledWrapper disabled={loading}>
                <CardCvcElement options={{ style, disabled: loading }} />
              </DisabledWrapper>
            </FormWrapper>
          </CardDetails>
          <div id='StripeAddressWrapper'>
            <h3
              className=' text-wfBlack w-full my-2'
              style={{
                fontSize: 19,
              }}
            >
              {i18next.t('Billing Address')}
            </h3>
            <AddressElement
              defaultValues={newCard.addressInfo}
              className=' mt-4'
              onChange={e => {
                newCard.SetAddressInfo(e);
                if (isFunction(handleChangeAddress)) {
                  handleChangeAddress(e);
                }
              }}
              onLoadError={e => {
                Alert.error(e.error.message);
              }}
              options={{
                mode: 'billing',
              }}
            />
          </div>

          {this.props.expandStripeBottom &&
            this.props.expandStripeBottom({
              stripeManager: newCard,
              hasSubmitted,
            })}
          <ProTip size='small' color='black'>
            <strong>Please note:</strong> your card may be charged a small
            amount for validation purposes.
          </ProTip>
          <Center>
            <div className='flex items-center justify-center min-w-fit'>
              {!hideAddButton && (
                <Button
                  variant='tertiary'
                  {...saveProps}
                  dataTestId='billingPage-addPaymentMethodConfirm-button'
                  onClick={() => {
                    this.submit(onSubmitCallback);
                  }}
                  disabled={newCard.submitting}
                  loading={saveProps?.loading || newCard.submitting}
                />
              )}
              {onCancel && (
                <Button
                  text='Cancel'
                  variant='Warning'
                  style={{ padding: 0, minWidth: 30, marginLeft: 16 }}
                  className='hover:text-red-500'
                  onClick={onCancel}
                />
              )}
            </div>
          </Center>
          {footer}
        </CardFormWrapper>
      </>
    );
  }
}

@observer
export class StripeWrapper extends React.Component {
  render() {
    const { children } = this.props;
    const { stripePromise } = stripeStore;
    // if (!stripe) return null;
    return (
      <StripeElement>
        <Elements
          options={{
            locale: clientStore.locale,
            fonts,
            appearance: {
              rules: {
                'div[data-field="name"]': {
                  display: 'none',
                },
                '.Label': {
                  fontWeight: 800,
                  marginTop: '6px',
                  textTransform: 'capitalize',
                  marginBottom: '10px',
                },
                'div[data-field="name"].p-Field': {
                  display: 'none',
                },
                '#Field-nameInput': {
                  display: 'none !important',
                },
                '.Input': {
                  border: `1px solid ${themes.color.wfGrey500}`,
                  borderRadius: '2px',
                },
                '.p-Input-input': {
                  border: `1px solid ${themes.color.wfGrey500}`,
                  borderRadius: '2px',
                },
              },
              variables: {
                fontSizeSm: '16px',
                colorText: themes.color.wfBlack,
                colorBackgroundDeemphasize10: themes.color.wfGrey500,
                fontFamily: '"HelveticaNowDisplay", Helvetica, sans-serif',
              },
            },
          }}
          stripe={stripePromise}
        >
          {children}
        </Elements>
      </StripeElement>
    );
  }
}

@observer
export class NewCardForm extends React.Component {
  render() {
    if (!STRIPE_API_KEY) {
      return (
        <div>
          The app wasn&apos;t built with Stripe support. Use
          --env.STRIPE_API_KEY=&apos;apikey&apos;
        </div>
      );
    }
    const {
      paymentMethodsManager,
      responsive,
      renderCardLogo,
      showFormTitle = true,
      expandStripeTop,
      expandStripeBottom,
      onSubmit,
      successAction,
      onCancel,
      onSubmitCallback,
      saveProps,
      footer,
      hideAddButton = false,
      hideDivider = false,
    } = this.props;
    return (
      <>
        {showFormTitle && <FormTitle>Add a new payment method:</FormTitle>}
        {!hideDivider && <FormDivider />}
        <StripeWrapper>
          <ElementsConsumer>
            {({ stripe, elements }) => (
              <StripeCardForm
                paymentMethodsManager={paymentMethodsManager}
                renderCardLogo={renderCardLogo}
                responsive={responsive}
                stripe={stripe}
                successAction={successAction}
                expandStripeTop={expandStripeTop}
                expandStripeBottom={expandStripeBottom}
                onSubmit={onSubmit}
                onSubmitCallback={onSubmitCallback}
                onCancel={onCancel}
                saveProps={saveProps}
                footer={footer}
                hideAddButton={hideAddButton}
                elements={elements}
              />
            )}
          </ElementsConsumer>
        </StripeWrapper>
      </>
    );
  }
}

StripeCardForm.propTypes = {
  paymentMethodsManager: PropTypes.object,
  stripe: PropTypes.object,
  responsive: PropTypes.bool,
  successAction: PropTypes.func,
  t: PropTypes.func,
  renderCardLogo: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmitCallback: PropTypes.func,
  expandStripeTop: PropTypes.func,
  expandStripeBottom: PropTypes.func,
  onSubmit: PropTypes.func,
  saveProps: PropTypes.object,
  hideAddButton: PropTypes.bool,
  footer: PropTypes.node,
  hideDivider: PropTypes.bool,
};

StripeWrapper.propTypes = {
  children: PropTypes.node,
};

NewCardForm.propTypes = {
  paymentMethodsManager: PropTypes.object,
  responsive: PropTypes.bool,
  renderCardLogo: PropTypes.bool,
  showFormTitle: PropTypes.bool,
  expandStripeTop: PropTypes.func,
  expandStripeBottom: PropTypes.func,
  successAction: PropTypes.func,
  onCancel: PropTypes.func,
  onSubmitCallback: PropTypes.func,
  onSubmit: PropTypes.func,
  saveProps: PropTypes.object,
  hideAddButton: PropTypes.bool,
  footer: PropTypes.node,
  hideDivider: PropTypes.bool,
};

const CardFormWrapper = styled(FormWrapper)`
  ${props =>
    props.posting &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}
  ${props =>
    props.responsive &&
    `
    width: 100%;
    max-width: 60vw;
  `}
  ${props => 'padding: 0;'}
`;

export const CardDetails = styled.div`
  display: flex;
  height: 85px;
  flex-direction: row;
  justify-content: flex-start;
  gap: 2em;
`;

const StripeElement = styled.div`
  .StripeElement {
    height: 40px;
    padding: 11px 10px 12px 10px;
    border: 1px solid ${props => props.theme.color.wfGrey500};
    border-radius: 2px;
    background-color: ${props => props.theme.color.wfWhite};
    margin: 5px 0 0 0;
    -webkit-transition: box-shadow 150ms ease;
    transition: box-shadow 150ms ease;
  }
  .StripeElement--focus {
    border: 1px solid ${props => props.theme.color.wfGrey200};
  }
  .StripeElement--invalid {
    border-color: ${props => props.theme.color.red};
  }
  .StripeElement--webkit-autofill {
    background-color: #fefde5 !important;
  }

  #StripeAddressWrapper {
    .StripeElement {
      height: fit-content;
      padding: 0;
      border: none;
      background-color: ${themes.color.wfWhite};
      margin: 5px 0 0 0;
      -webkit-transition: box-shadow 150ms ease;
      transition: box-shadow 150ms ease;
    }
  }
`;

const CardsWrapper = styled.div`
  margin-bottom: 10px;
  margin-top: 15px;
`;

export const CardLabel = styled(Label)`
  margin-bottom: 0.5em;
  font-weight: 600;
`;
